import React, { useEffect, useState, useCallback } from "react";
import AppLayout from "@/components/AppLayout";
import { api } from "@/lib/api";
import { fmtEUR2, fmtPct, fmtNum } from "@/lib/format";
import {
  Plus, Trash2, Save, Loader2, Calculator, Settings2, AlertCircle, ChevronDown, ChevronUp,
  FileDown,
} from "lucide-react";
import { toast } from "sonner";

const empty = () => ({
  label: "", importMin: 0, importMax: 0, durataMinMesi: 24, durataMaxMesi: 120,
  tan: 0, feeIstr: 0, speseIncMens: 3.5, bollo: 16, provPerc: 0, note: "", active: true,
});

export default function PpQuotePage() {
  const [rates, setRates] = useState([]);
  const [showRates, setShowRates] = useState(false);
  const [editing, setEditing] = useState(null);     // rate object being edited
  const [creating, setCreating] = useState(null);   // empty object
  const [loadingR, setLoadingR] = useState(false);

  // Quote state
  const [importoNetto, setImportoNetto] = useState(11000);
  const [durata, setDurata] = useState(96);
  const [decorrenza, setDecorrenza] = useState("");
  const [tanOv, setTanOv] = useState("");
  const [feeOv, setFeeOv] = useState("");
  const [provOv, setProvOv] = useState("");
  const [speseOv, setSpeseOv] = useState("");
  const [bolloOv, setBolloOv] = useState("");
  const [quote, setQuote] = useState(null);
  const [calcLoading, setCalcLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [nominativo, setNominativo] = useState("");

  // Tabelle Creditis ufficiali
  const [creditisTables, setCreditisTables] = useState([]);
  const [tableCode, setTableCode] = useState("");           // codice tabella selezionato
  const [speseIstrPerc, setSpeseIstrPerc] = useState(2.5);  // % spese istruttoria

  useEffect(() => {
    api.get("/pp/tables").then((r) => setCreditisTables(r.data.items || [])).catch(() => {});
  }, []);

  // Filtra tabelle compatibili con importo + durata corrente
  const compatibleTables = creditisTables.filter((t) => {
    const imp = Number(importoNetto);
    const dur = Number(durata);
    if (imp < t.importoMin || imp > t.importoMax) return false;
    if (dur > t.maxDurata) return false;
    if (dur > 84 && !t.tanStep3) return false;
    return true;
  });

  const loadRates = useCallback(async () => {
    setLoadingR(true);
    try {
      const r = await api.get("/pp/rates");
      setRates(r.data.items || []);
    } finally { setLoadingR(false); }
  }, []);
  useEffect(() => { loadRates(); }, [loadRates]);

  const seedDefaults = async () => {
    const r = await api.post("/pp/rates/seed-defaults");
    if (r.data.created > 0) toast.success(`${r.data.created} fasce predefinite create. Modificale per i tuoi tassi reali.`);
    else toast.message(r.data.msg || "Esistono già fasce");
    loadRates();
  };

  const saveRate = async (rate, isNew) => {
    try {
      if (isNew) await api.post("/pp/rates", rate);
      else await api.patch(`/pp/rates/${rate.id}`, rate);
      toast.success("Fascia salvata");
      setEditing(null);
      setCreating(null);
      loadRates();
    } catch { toast.error("Errore salvataggio fascia"); }
  };
  const deleteRate = async (id) => {
    if (!window.confirm("Eliminare la fascia?")) return;
    await api.delete(`/pp/rates/${id}`);
    toast.success("Fascia eliminata");
    loadRates();
  };
  const toggleActive = async (r) => {
    await api.patch(`/pp/rates/${r.id}`, { active: !r.active });
    loadRates();
  };

  const computeQuote = async (e) => {
    e?.preventDefault?.();
    setCalcLoading(true);
    setQuote(null);
    try {
      const payload = { importoNetto: Number(importoNetto), durataMesi: Number(durata) };
      if (tableCode) {
        payload.tableCode = tableCode;
        payload.speseIstrPerc = Number(speseIstrPerc);
      }
      if (decorrenza) payload.dataDecorrenza = decorrenza;
      if (tanOv !== "") payload.tan = Number(tanOv);
      if (feeOv !== "") payload.feeIstr = Number(feeOv);
      if (provOv !== "") payload.provPerc = Number(provOv);
      if (speseOv !== "") payload.speseIncMens = Number(speseOv);
      if (bolloOv !== "") payload.bollo = Number(bolloOv);
      const r = await api.post("/pp/quote", payload);
      setQuote(r.data);
    } catch (e2) {
      toast.error(e2?.response?.data?.detail || "Errore calcolo");
    } finally { setCalcLoading(false); }
  };

  const downloadPdf = async () => {
    setPdfLoading(true);
    try {
      const payload = { importoNetto: Number(importoNetto), durataMesi: Number(durata) };
      if (tableCode) {
        payload.tableCode = tableCode;
        payload.speseIstrPerc = Number(speseIstrPerc);
      }
      if (decorrenza) payload.dataDecorrenza = decorrenza;
      if (tanOv !== "") payload.tan = Number(tanOv);
      if (feeOv !== "") payload.feeIstr = Number(feeOv);
      if (provOv !== "") payload.provPerc = Number(provOv);
      if (speseOv !== "") payload.speseIncMens = Number(speseOv);
      if (bolloOv !== "") payload.bollo = Number(bolloOv);
      if (nominativo) payload.nominativo = nominativo;
      const r = await api.post("/pdf/pp-quote", payload, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `preventivo-pp-${nominativo ? nominativo.replace(/\s+/g, "_") + "-" : ""}${new Date().toISOString().slice(0, 10)}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("PDF generato");
    } catch (e2) { toast.error(e2?.response?.data?.detail || "Errore PDF"); }
    finally { setPdfLoading(false); }
  };

  return (
    <AppLayout
      title="Preventivatore PP"
      subtitle="Prestiti Personali"
      actions={
        <button
          onClick={() => setShowRates((s) => !s)}
          data-testid="btn-toggle-rates"
          className="px-4 py-2 text-sm border border-slate-300 rounded-[3px] flex items-center gap-2 text-slate-700 hover:bg-slate-50 bg-white"
        >
          <Settings2 className="w-4 h-4" /> Tariffario TAN
          {showRates ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>
      }
    >
      {/* CALCULATOR */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 animate-fade-in">
        <div className="lg:col-span-1 bg-white border border-slate-200 rounded-[3px] shadow-[0_1px_3px_rgba(10,25,47,0.05)] p-6">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-9 h-9 bg-[#F5EFE3] text-[#8a6e3d] rounded-[3px] flex items-center justify-center"><Calculator className="w-4 h-4" /></div>
            <h3 className="font-serif text-lg text-slate-900">Nuovo Preventivo</h3>
          </div>
          <form onSubmit={computeQuote} className="space-y-3">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-slate-500">Importo NETTO al cliente (€)</label>
              <input type="number" min="100" step="100" required value={importoNetto} onChange={(e) => setImportoNetto(e.target.value)} data-testid="pp-importo" className="w-full mt-1 px-3 py-2 text-sm border border-slate-300 rounded-[3px] tabular-nums" />
              <div className="text-[10px] text-slate-400 mt-1">L'erogato che il cliente riceve (le commissioni vengono sommate sopra come Finanziato)</div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-[10px] uppercase tracking-wider text-slate-500">Durata</label>
                <select value={durata} onChange={(e) => setDurata(e.target.value)} data-testid="pp-durata" className="w-full mt-1 px-3 py-2 text-sm border border-slate-300 rounded-[3px]">
                  {[12, 24, 36, 48, 60, 72, 84, 96, 108, 120].map((m) => <option key={m}>{m}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-slate-500">Decorrenza</label>
                <input type="date" value={decorrenza} onChange={(e) => setDecorrenza(e.target.value)} data-testid="pp-decorrenza" className="w-full mt-1 px-3 py-2 text-sm border border-slate-300 rounded-[3px]" />
              </div>
            </div>

            {/* Tabelle Creditis ufficiali */}
            <div className="pt-2 border-t border-[#F0F2F5]">
              <label className="text-[10px] uppercase tracking-wider text-[#8a6e3d] font-bold flex items-center gap-1">
                ✦ Tabella Creditis (Pricing PP 07/04/2026)
              </label>
              <select
                value={tableCode}
                onChange={(e) => setTableCode(e.target.value)}
                data-testid="pp-table-code"
                className="w-full mt-1 px-3 py-2 text-sm border border-[#B7955B] bg-[#FDF6EC] rounded-[3px] font-medium"
              >
                <option value="">— Auto-match (fascia personalizzata) —</option>
                <optgroup label="Prodotto SN (nuovi clienti)">
                  {creditisTables.filter((t) => t.product === "SN").map((t) => {
                    const dur = Number(durata);
                    const tan = dur <= 84 ? t.tanBase : t.tanStep3;
                    return (
                      <option key={t.code} value={t.code}>
                        Tabella {t.fascia} ({t.code}) · TAN {tan}% · max €{(t.importoMax/1000).toFixed(0)}k
                      </option>
                    );
                  })}
                </optgroup>
                <optgroup label="Prodotto BN (clienti esistenti)">
                  {creditisTables.filter((t) => t.product === "BN").map((t) => {
                    const dur = Number(durata);
                    const tan = dur <= 84 ? t.tanBase : t.tanStep3;
                    return (
                      <option key={t.code} value={t.code}>
                        Tabella {t.fascia} ({t.code}) · TAN {tan}% · max €{(t.importoMax/1000).toFixed(0)}k
                      </option>
                    );
                  })}
                </optgroup>
              </select>
              {tableCode && (() => {
                const sel = creditisTables.find((t) => t.code === tableCode);
                const imp = Number(importoNetto);
                const dur = Number(durata);
                const fuoriImporto = sel && (imp < sel.importoMin || imp > sel.importoMax);
                const fuoriDurata = sel && (dur > sel.maxDurata || (dur > 84 && !sel.tanStep3));
                return (
                  <div className="mt-2 space-y-2">
                    {(fuoriImporto || fuoriDurata) && (
                      <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 px-2 py-1.5 rounded-[3px]">
                        ⚠ Attenzione: {fuoriImporto && `importo fuori range (${sel.importoMin.toLocaleString("it-IT")}–${sel.importoMax.toLocaleString("it-IT")} €)`}
                        {fuoriImporto && fuoriDurata && " · "}
                        {fuoriDurata && `durata non ammessa (max ${sel.maxDurata} mesi)`}
                      </div>
                    )}
                    <div>
                      <label className="text-[10px] uppercase tracking-wider text-slate-500">
                        Spese istruttoria % (max 2,5% — cap €1.500)
                      </label>
                      <input
                        type="number" step="0.1" min="0" max="2.5"
                        value={speseIstrPerc}
                        onChange={(e) => setSpeseIstrPerc(e.target.value)}
                        data-testid="pp-spese-istr-perc"
                        className="w-full mt-1 px-2 py-2 text-sm border border-slate-300 rounded-[3px] tabular-nums"
                      />
                      <div className="text-[10px] text-slate-400 mt-0.5">
                        Provvigione = % sul <b>netto cliente</b>. Le spese istruttoria vengono pagate al <b>65%</b> come ulteriore provvigione.
                      </div>
                    </div>
                  </div>
                );
              })()}
            </div>
            <div className="pt-2 border-t border-[#F0F2F5]">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-2">Override (opzionali) — sovrascrivono la fascia</div>
              <div className="grid grid-cols-2 gap-2">
                <input type="number" step="0.01" placeholder="TAN %" value={tanOv} onChange={(e) => setTanOv(e.target.value)} data-testid="pp-tan-override" className="px-2 py-2 text-sm border border-slate-300 rounded-[3px] tabular-nums" />
                <input type="number" step="0.01" placeholder="Commiss. €" value={feeOv} onChange={(e) => setFeeOv(e.target.value)} data-testid="pp-fee-override" className="px-2 py-2 text-sm border border-slate-300 rounded-[3px] tabular-nums" />
                <input type="number" step="0.01" placeholder="Spese incasso €" value={speseOv} onChange={(e) => setSpeseOv(e.target.value)} className="px-2 py-2 text-sm border border-slate-300 rounded-[3px] tabular-nums" />
                <input type="number" step="0.01" placeholder="Bollo €" value={bolloOv} onChange={(e) => setBolloOv(e.target.value)} className="px-2 py-2 text-sm border border-slate-300 rounded-[3px] tabular-nums" />
                <input type="number" step="0.01" placeholder="Prov %" value={provOv} onChange={(e) => setProvOv(e.target.value)} data-testid="pp-prov-override" className="col-span-2 px-2 py-2 text-sm border border-slate-300 rounded-[3px] tabular-nums" />
              </div>
            </div>
            <button type="submit" disabled={calcLoading} data-testid="pp-calc" className="w-full text-white py-2.5 rounded-[3px] flex items-center justify-center gap-2 disabled:opacity-60" style={{ background: "var(--navy)" }} onMouseEnter={(e)=>e.currentTarget.style.background="var(--navy-3)"} onMouseLeave={(e)=>e.currentTarget.style.background="var(--navy)"} >
              {calcLoading && <Loader2 className="w-4 h-4 animate-spin" />} Calcola Preventivo
            </button>
          </form>
        </div>

        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-[3px] shadow-[0_1px_3px_rgba(10,25,47,0.05)] p-6">
          <h3 className="font-serif text-lg text-slate-900 mb-4">Risultato</h3>
          {!quote ? (
            <div className="text-sm text-slate-500 italic flex items-start gap-2">
              <AlertCircle className="w-4 h-4 mt-0.5" /> Inserisci importo e durata per generare il preventivo.
            </div>
          ) : (
            <div>
              {quote.rate ? (
                <div className="mb-4 p-3 bg-[#F5EFE3] border border-[#E5D7B6] rounded-[3px] flex items-start justify-between text-sm">
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-[#8a6e3d]">Fascia applicata</div>
                    <div className="font-medium text-slate-900">{quote.rate.label}</div>
                    <div className="text-xs text-slate-600 tabular-nums">TAN {quote.rate.tan}% • Prov {quote.rate.provPerc}% • Fee €{quote.rate.feeIstr}</div>
                  </div>
                </div>
              ) : (
                <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-[3px] text-sm text-amber-800">
                  Nessuna fascia matching: applicati solo gli override.
                </div>
              )}

              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                <ResultCard label="Erogato Netto" value={fmtEUR2(quote.result.erogatoNetto)} />
                <ResultCard label="Finanziato" value={fmtEUR2(quote.result.finanziato)} />
                <ResultCard label="Numero rate" value={`${quote.input.durataMesi}`} />
                <ResultCard label="Rata Base" value={fmtEUR2(quote.result.rataBase)} />
                <ResultCard label="Rata Totale" value={fmtEUR2(quote.result.rataTotale)} accent />
                <ResultCard label="TAEG" value={quote.result.taeg != null ? `${fmtNum(quote.result.taeg, 3)}%` : "—"} accent />
                <ResultCard label="Interessi scalari" value={fmtEUR2(quote.result.interessi)} />
                <ResultCard label="Tot. incasso rate" value={fmtEUR2(quote.result.totIncassoRate)} />
                <ResultCard label="Costi finanziamento" value={fmtEUR2(quote.result.costiFinanziamento)} />
                <ResultCard label="Montante" value={fmtEUR2(quote.result.montante)} />
                <ResultCard label="Totale pagato" value={fmtEUR2(quote.result.totalePagato)} />
                <ResultCard label="Provvigioni" value={fmtEUR2(quote.result.provvigioni)} accent />
                <ResultCard label="Utile Istruttoria 65%" value={fmtEUR2(quote.result.utileIstruttoria65)} />
                <ResultCard label="Utile Totale" value={fmtEUR2(quote.result.utileTotale)} accent />
              </div>

              <div className="mt-4 grid grid-cols-3 gap-3 text-center text-sm">
                <div className="border border-slate-200 rounded-[3px] p-3 bg-slate-50">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500">Decorrenza</div>
                  <div className="tabular-nums font-medium text-slate-900 mt-1">{quote.result.dataDecorrenza ? new Date(quote.result.dataDecorrenza).toLocaleDateString("it-IT") : "—"}</div>
                </div>
                <div className="border border-slate-200 rounded-[3px] p-3 bg-slate-50">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500">Prima scadenza</div>
                  <div className="tabular-nums font-medium text-slate-900 mt-1">{quote.result.primaScadenza ? new Date(quote.result.primaScadenza).toLocaleDateString("it-IT") : "—"}</div>
                </div>
                <div className="border border-slate-200 rounded-[3px] p-3 bg-slate-50">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500">Ultima scadenza</div>
                  <div className="tabular-nums font-medium text-slate-900 mt-1">{quote.result.ultimaScadenza ? new Date(quote.result.ultimaScadenza).toLocaleDateString("it-IT") : "—"}</div>
                </div>
              </div>

              <div className="mt-5 pt-4 border-t border-[#F0F2F5] flex flex-col md:flex-row md:items-end gap-3">
                <div className="flex-1">
                  <label className="text-[10px] uppercase tracking-wider text-slate-500">Nominativo cliente (per il PDF)</label>
                  <input
                    value={nominativo}
                    onChange={(e) => setNominativo(e.target.value)}
                    placeholder="Es. Mario Rossi"
                    data-testid="pp-nominativo"
                    className="w-full mt-1 px-3 py-2 text-sm border border-slate-300 rounded-[3px] focus:border-[#0A192F] focus:ring-2 focus:ring-slate-900/8"
                  />
                </div>
                <button
                  onClick={downloadPdf}
                  disabled={pdfLoading}
                  data-testid="pp-pdf"
                  className="bg-[#B7955B] hover:bg-[#A18048] text-white px-4 py-2.5 rounded-[3px] flex items-center justify-center gap-2 disabled:opacity-60"
                >
                  {pdfLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileDown className="w-4 h-4" />}
                  Genera PDF
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* RATES MANAGER */}
      {showRates && (
        <div className="mt-6 bg-white border border-slate-200 rounded-[3px] shadow-[0_1px_3px_rgba(10,25,47,0.05)] animate-fade-in" data-testid="rates-manager">
          <div className="flex items-center justify-between border-b border-[#F0F2F5] px-5 py-3.5">
            <div>
              <h3 className="font-serif text-lg text-slate-900">Tariffario Fasce TAN</h3>
              <div className="text-xs text-slate-500">Modifica TAN, spese e provvigioni quando i tassi cambiano. Le modifiche valgono solo per il tuo account.</div>
            </div>
            <div className="flex gap-2">
              {rates.length === 0 && (
                <button onClick={seedDefaults} data-testid="btn-seed-defaults" className="text-xs px-3 py-1.5 border border-slate-300 rounded-[3px] hover:bg-slate-50">
                  Crea fasce di esempio
                </button>
              )}
              <button onClick={() => setCreating(empty())} data-testid="btn-new-rate" className="text-xs text-white px-3 py-1.5 rounded-[3px] flex items-center gap-1.5" style={{ background: "var(--navy)" }} onMouseEnter={(e)=>e.currentTarget.style.background="var(--navy-3)"} onMouseLeave={(e)=>e.currentTarget.style.background="var(--navy)"} >
                <Plus className="w-3.5 h-3.5" /> Nuova fascia
              </button>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[10px] uppercase tracking-[0.1em] text-slate-500 border-b border-slate-200 bg-slate-50">
                  <th className="text-left font-semibold py-2.5 px-3">Etichetta</th>
                  <th className="text-right font-semibold py-2.5 px-3">Da €</th>
                  <th className="text-right font-semibold py-2.5 px-3">A €</th>
                  <th className="text-right font-semibold py-2.5 px-3">Mesi (min-max)</th>
                  <th className="text-right font-semibold py-2.5 px-3">TAN %</th>
                  <th className="text-right font-semibold py-2.5 px-3">Fee €</th>
                  <th className="text-right font-semibold py-2.5 px-3">Sp. Inc. €</th>
                  <th className="text-right font-semibold py-2.5 px-3">Bollo €</th>
                  <th className="text-right font-semibold py-2.5 px-3">Prov %</th>
                  <th className="text-center font-semibold py-2.5 px-3">Attiva</th>
                  <th className="w-20"></th>
                </tr>
              </thead>
              <tbody>
                {creating && <RateEditRow row={creating} onSave={(r) => saveRate(r, true)} onCancel={() => setCreating(null)} />}
                {loadingR ? (
                  <tr><td colSpan={11} className="text-center py-8 inline text-slate-400"><Loader2 className="w-5 h-5 animate-spin" /></td></tr>
                ) : rates.length === 0 && !creating ? (
                  <tr><td colSpan={11} className="text-center text-slate-500 py-8">Nessuna fascia. Crea la prima o usa quelle di esempio.</td></tr>
                ) : (
                  rates.map((r) => editing?.id === r.id
                    ? <RateEditRow key={r.id} row={editing} onChange={setEditing} onSave={(x) => saveRate(x, false)} onCancel={() => setEditing(null)} />
                    : <RateRow key={r.id} r={r} onEdit={() => setEditing({ ...r })} onDelete={() => deleteRate(r.id)} onToggle={() => toggleActive(r)} />
                  )
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </AppLayout>
  );
}

function ResultCard({ label, value, accent = false }) {
  return (
    <div className={`border ${accent ? "border-[#B7955B] bg-[#FBF7EE]" : "border-slate-200 bg-slate-50"} rounded-[3px] p-4`} data-testid={`pp-res-${label.replace(/\s+/g, "-").toLowerCase()}`}>
      <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className={`font-serif text-xl mt-1 tabular-nums ${accent ? "text-[#8a6e3d]" : "text-slate-900"}`}>{value}</div>
    </div>
  );
}

function RateRow({ r, onEdit, onDelete, onToggle }) {
  return (
    <tr className="border-b border-[#F0F2F5] tbl-row" data-testid={`rate-${r.id}`}>
      <td className="py-2.5 px-3 font-medium text-slate-900">{r.label || "—"}</td>
      <td className="py-2.5 px-3 text-right tabular-nums">{fmtEUR2(r.importMin)}</td>
      <td className="py-2.5 px-3 text-right tabular-nums">{fmtEUR2(r.importMax)}</td>
      <td className="py-2.5 px-3 text-right tabular-nums">{r.durataMinMesi}-{r.durataMaxMesi}</td>
      <td className="py-2.5 px-3 text-right tabular-nums font-semibold">{fmtNum(r.tan, 2)}</td>
      <td className="py-2.5 px-3 text-right tabular-nums">{fmtNum(r.feeIstr, 0)}</td>
      <td className="py-2.5 px-3 text-right tabular-nums">{fmtNum(r.speseIncMens, 2)}</td>
      <td className="py-2.5 px-3 text-right tabular-nums">{fmtNum(r.bollo, 0)}</td>
      <td className="py-2.5 px-3 text-right tabular-nums text-[#8a6e3d] font-medium">{fmtNum(r.provPerc, 2)}</td>
      <td className="py-2.5 px-3 text-center">
        <button onClick={onToggle} className={`text-xs px-2 py-0.5 rounded-[3px] border ${r.active ? "bg-emerald-50 border-emerald-200 text-emerald-700" : "bg-slate-100 border-slate-200 text-slate-500"}`}>
          {r.active ? "Sì" : "No"}
        </button>
      </td>
      <td className="py-2.5 px-3 flex items-center gap-1">
        <button onClick={onEdit} className="p-1.5 hover:bg-slate-100 rounded-[3px] text-slate-600" title="Modifica"><Settings2 className="w-3.5 h-3.5" /></button>
        <button onClick={onDelete} className="p-1.5 hover:bg-rose-50 rounded-[3px] text-rose-600" title="Elimina"><Trash2 className="w-3.5 h-3.5" /></button>
      </td>
    </tr>
  );
}

function RateEditRow({ row, onChange, onSave, onCancel }) {
  const [v, setV] = useState(row);
  useEffect(() => { setV(row); }, [row]);
  const set = (k, val) => { const n = { ...v, [k]: val }; setV(n); onChange?.(n); };
  const inp = "w-full px-2 py-1.5 text-xs border border-slate-300 rounded-[3px] tabular-nums";
  return (
    <tr className="border-b border-[#F0F2F5] bg-amber-50/30">
      <td className="py-2 px-2"><input value={v.label || ""} onChange={(e) => set("label", e.target.value)} placeholder="Etichetta" className={inp} /></td>
      <td className="py-2 px-2"><input type="number" value={v.importMin} onChange={(e) => set("importMin", Number(e.target.value))} className={`${inp} text-right`} /></td>
      <td className="py-2 px-2"><input type="number" value={v.importMax} onChange={(e) => set("importMax", Number(e.target.value))} className={`${inp} text-right`} /></td>
      <td className="py-2 px-2 flex gap-1">
        <input type="number" value={v.durataMinMesi} onChange={(e) => set("durataMinMesi", Number(e.target.value))} className={`${inp} text-right`} />
        <input type="number" value={v.durataMaxMesi} onChange={(e) => set("durataMaxMesi", Number(e.target.value))} className={`${inp} text-right`} />
      </td>
      <td className="py-2 px-2"><input type="number" step="0.01" value={v.tan} onChange={(e) => set("tan", Number(e.target.value))} className={`${inp} text-right font-semibold`} /></td>
      <td className="py-2 px-2"><input type="number" step="0.01" value={v.feeIstr} onChange={(e) => set("feeIstr", Number(e.target.value))} className={`${inp} text-right`} /></td>
      <td className="py-2 px-2"><input type="number" step="0.01" value={v.speseIncMens} onChange={(e) => set("speseIncMens", Number(e.target.value))} className={`${inp} text-right`} /></td>
      <td className="py-2 px-2"><input type="number" step="0.01" value={v.bollo} onChange={(e) => set("bollo", Number(e.target.value))} className={`${inp} text-right`} /></td>
      <td className="py-2 px-2"><input type="number" step="0.01" value={v.provPerc} onChange={(e) => set("provPerc", Number(e.target.value))} className={`${inp} text-right`} /></td>
      <td className="py-2 px-2 text-center">
        <input type="checkbox" checked={!!v.active} onChange={(e) => set("active", e.target.checked)} />
      </td>
      <td className="py-2 px-2 flex items-center gap-1">
        <button onClick={() => onSave(v)} data-testid="rate-save" className="p-1.5 text-white rounded-[3px]" style={{ background: "var(--navy)" }} onMouseEnter={(e)=>e.currentTarget.style.background="var(--navy-3)"} onMouseLeave={(e)=>e.currentTarget.style.background="var(--navy)"}  title="Salva"><Save className="w-3.5 h-3.5" /></button>
        <button onClick={onCancel} className="text-xs text-slate-500 px-1">×</button>
      </td>
    </tr>
  );
}
